/**
 * Google Ads conversion tracking.
 *
 * The tag is privacy-gated: `initGoogleAds` is only called after the user has
 * accepted cookies. Until then `window.gtag` is undefined and
 * `trackGoogleAdsLead` is a no-op.
 */

const googleAdsId = 'AW-18502138207';
const googleAdsLeadConversionLabel = 'tiZmCL308ZUdEN_ywPZE';
const googleAdsSignupConversionLabel = 'LIIUCO-h4ZwdEN_ywPZE';
const gtagSrc = 'https://www.googletagmanager.com/gtag/js';

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

let isInitialized = false;

/**
 * Replicates Google's official bootstrap snippet and asynchronously loads
 * gtag.js. Idempotent.
 */
function loadGtag(): void {
  if (window.gtag) {
    return;
  }

  window.dataLayer ??= [];
  window.gtag = function gtag(): void {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  };

  const script = document.createElement('script');
  script.async = true;
  script.src = `${gtagSrc}?id=${googleAdsId}`;
  document.head.appendChild(script);
}

/**
 * Initialises Google Ads. MUST only be called after the user has accepted
 * cookies. Safe to call multiple times.
 */
export function initGoogleAds(): void {
  if (typeof window === 'undefined' || isInitialized) {
    return;
  }
  isInitialized = true;

  loadGtag();
  window.gtag?.('js', new Date());
  window.gtag?.('config', googleAdsId);
}

/**
 * Tracks a successful waitlist signup as a Google Ads conversion.
 */
export function trackGoogleAdsLead(): void {
  trackGoogleAdsConversion(googleAdsLeadConversionLabel);
}

/**
 * Tracks a successful account signup as its dedicated Google Ads conversion.
 * `transactionId` lets Google drop duplicate conversions for the same user.
 */
export function trackGoogleAdsSignup(transactionId: string): void {
  trackGoogleAdsConversion(googleAdsSignupConversionLabel, {
    transaction_id: transactionId,
  });
}

const trackGoogleAdsConversion = (
  conversionLabel: string,
  extraParams: Record<string, string> = {},
): void => {
  if (typeof window === 'undefined') {
    return;
  }
  window.gtag?.('event', 'conversion', {
    send_to: `${googleAdsId}/${conversionLabel}`,
    value: 1,
    currency: 'GBP',
    ...extraParams,
  });
};
